import { useTranslation } from 'react-i18next'
import { articlesPath, currentLocale, localeRootPath } from './i18n'

type PublicFooterProps = {
  standalone?: boolean
}

function FooterLogo({ href }: { href: string }) {
  const { t } = useTranslation()
  return (
    <a className="logo" href={href} aria-label={t('accessibility.backToTop')}>
      <span className="brand-mark brand-mark--compact" aria-hidden="true" />
      <span>SIDEWISP</span>
    </a>
  )
}

function GolemWorkersLockup() {
  return (
    <span className="golem-workers-lockup">
      <img src="/brand/golem-workers-mark.svg" width="28" height="31" alt="Golem Workers" />
      <strong>GOLEM WORKERS</strong>
    </span>
  )
}

function ArrowIcon() {
  return (
    <svg className="arrow-icon" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M3.75 9h10.5M10 4.75 14.25 9 10 13.25" />
    </svg>
  )
}

function GitHubIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 .3a12 12 0 0 0-3.8 23.4c.6.1.8-.3.8-.6v-2.3c-3.3.7-4-1.4-4-1.4-.5-1.4-1.3-1.8-1.3-1.8-1.1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1.1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.8-1.6-2.7-.3-5.5-1.3-5.5-5.9 0-1.3.5-2.4 1.2-3.2-.1-.3-.5-1.5.1-3.2 0 0 1-.3 3.3 1.2a11.4 11.4 0 0 1 6 0c2.3-1.5 3.3-1.2 3.3-1.2.6 1.7.2 2.9.1 3.2.8.8 1.2 1.9 1.2 3.2 0 4.6-2.8 5.6-5.5 5.9.4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6A12 12 0 0 0 12 .3Z" />
    </svg>
  )
}

function DiscordIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20.3 4.4A19.8 19.8 0 0 0 15.4 3l-.6 1.3a18.3 18.3 0 0 0-5.5 0L8.6 3a19.7 19.7 0 0 0-4.9 1.4C.6 9 .8 13.6 1.2 18.2a19.9 19.9 0 0 0 6 3l1.5-2a12.8 12.8 0 0 1-2.3-1.1l.6-.5a14.2 14.2 0 0 0 12 0l.7.5a15 15 0 0 1-2.4 1.1l1.5 2a19.8 19.8 0 0 0 6-3c.5-5.3-.8-9.8-4.5-13.8ZM8.7 15.4c-1.2 0-2.2-1.1-2.2-2.5s1-2.5 2.2-2.5 2.2 1.1 2.2 2.5-1 2.5-2.2 2.5Zm6.6 0c-1.2 0-2.2-1.1-2.2-2.5s1-2.5 2.2-2.5 2.2 1.1 2.2 2.5-1 2.5-2.2 2.5Z" />
    </svg>
  )
}

export default function PublicFooter({ standalone = false }: PublicFooterProps) {
  const { t } = useTranslation()
  const locale = currentLocale()
  const localeRoot = localeRootPath(locale)
  const sectionPrefix = standalone ? localeRoot : ''

  return (
    <footer className={`site-footer${standalone ? ' site-footer--standalone' : ''}`}>
      <div className="site-footer__brand">
        <FooterLogo href={standalone ? localeRoot : '#top'} />
        <p>AI agent health, explained clearly.<br />Every action stays yours.</p>
        <nav className="site-footer__socials" aria-label="Community">
          <a
            href="https://github.com/golem-workers/sidewisp-plugin"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Sidewisp on GitHub"
          >
            <GitHubIcon />
            <span>GITHUB</span>
          </a>
          <a
            href="https://discord.com/invite/NdCzPk3b6v"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Join Sidewisp on Discord"
          >
            <DiscordIcon />
            <span>DISCORD</span>
          </a>
        </nav>
      </div>
      <nav className="site-footer__links" aria-label="Footer">
        <span>EXPLORE</span>
        <a href={`${sectionPrefix}#product`}>PRODUCT</a>
        <a href={`${sectionPrefix}#checks`}>HEALTH CHECKS</a>
        <a href={`${sectionPrefix}#integrations`}>INTEGRATIONS</a>
        <a href={articlesPath(locale)}>{t('nav.articles').toUpperCase()}</a>
        <a href="/docs/">DOCS</a>
      </nav>
      <nav className="site-footer__links" aria-label="Legal">
        <span>LEGAL</span>
        <a href="/terms/">TERMS OF SERVICE</a>
        <a href="/privacy/">PRIVACY POLICY</a>
        <a href="mailto:info@sidewisp.com">CONTACT</a>
      </nav>
      <a
        className="site-footer__powered"
        href="https://golemworkers.com/"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="site-footer__powered-label">POWERED BY</span>
        <div className="site-footer__powered-body">
          <GolemWorkersLockup />
          <p>A platform for launching AI agents.</p>
        </div>
        <span className="site-footer__powered-arrow" aria-hidden="true"><ArrowIcon /></span>
      </a>
      <div className="site-footer__bottom">
        <span>© 2026 SIDEWISP · A GOLEMWORKERS LLC PRODUCT</span>
        <span>{t('final.loop')}</span>
        <a href="mailto:info@sidewisp.com">INFO@SIDEWISP.COM</a>
      </div>
    </footer>
  )
}
